import { logger } from '../logger';
import type { FavoriteItem, FavoritesState, FavoriteMetadata } from '../../types/favorites';
import type { NavigationType } from '../../types/shared/navigation';
import type { SupportedLanguages } from '../../i18n';

const STORAGE_KEY = 'app_favorites';

interface AddFavoriteParams {
  id: string;
  sourceType: NavigationType;
  data: any;
  metadata: FavoriteMetadata;
}

class FavoritesService {
  private static instance: FavoritesService;
  private state: FavoritesState;
  private cachedFavorites: FavoriteItem[] | null = null;

  private constructor() {
    this.state = this.loadState();
  }

  public static getInstance(): FavoritesService {
    if (!FavoritesService.instance) {
      FavoritesService.instance = new FavoritesService();
    }
    return FavoritesService.instance;
  }

  private loadState(): FavoritesState {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return { items: [], lastUpdated: new Date().toISOString() };
      }

      const parsed = JSON.parse(stored);

      logger.debug('Loaded favorites state', {
        category: 'Cache',
        data: {
          itemCount: parsed.items.length,
          items: parsed.items.map((item: any) => ({
            id: item.id,
            sourceType: item.sourceType,
            metadata: item.metadata,
            data: {
              name: item.data.name,
              category: item.data.category,
              type: item.data.type
            }
          }))
        }
      });

      return parsed;
    } catch (error) {
      logger.error('Error loading favorites', {
        category: 'Cache',
        data: { error }
      });
      return { items: [], lastUpdated: new Date().toISOString() };
    }
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      
      logger.debug('Favorites saved', {
        category: 'Cache',
        data: { 
          itemCount: this.state.items.length,
          items: this.state.items.map(item => ({
            id: item.id,
            sourceType: item.sourceType,
            metadata: item.metadata,
            data: {
              name: item.data.name,
              category: item.data.category,
              type: item.data.type
            }
          }))
        }
      });

      // Cache images in the Service Worker
      this.cacheImages();
    } catch (error) {
      logger.error('Error saving favorites', {
        category: 'Cache',
        data: { error }
      });
    }
  }

  private cacheImages(): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      this.state.items.forEach(item => {
        if (item.data.image) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CACHE_FAVORITE_IMAGE',
            url: item.data.image
          });
        }
      });
    }
  }

  public addFavorite(params: AddFavoriteParams): void {
    try {
      // Check if item already exists
      const exists = this.state.items.some(i => i.id === params.id);
      if (exists) {
        logger.debug('Item already in favorites', {
          category: 'User',
          data: { itemId: params.id }
        });
        return;
      }

      logger.debug('Adding item to favorites', {
        category: 'User',
        data: {
          itemId: params.id,
          sourceType: params.sourceType,
          metadata: params.metadata,
          data: {
            name: params.data.name,
            category: params.data.category,
            type: params.data.type
          }
        }
      });

      // Standardize the data structure based on source type
      const standardizedData = this.standardizeItemData(params.data, params.sourceType);

      // Add the new item
      this.state.items.push({
        id: params.id,
        sourceType: params.sourceType,
        data: standardizedData,
        addedAt: new Date().toISOString(),
        metadata: params.metadata
      });

      this.state.lastUpdated = new Date().toISOString();
      this.saveState();
      this.cachedFavorites = null; // Invalidate cache

      logger.debug('Item added to favorites', {
        category: 'User',
        data: { 
          itemId: params.id,
          sourceType: params.sourceType,
          metadata: params.metadata,
          hasImage: !!params.data.image
        }
      });
    } catch (error) {
      logger.error('Error adding favorite', {
        category: 'Cache',
        data: { error, params }
      });
    }
  }

  private standardizeItemData(data: any, sourceType: NavigationType): any {
    // Create a base structure with common fields
    const standardizedData = {
      id: data.id,
      name: data.name,
      description: data.description || '',
      descriptions: data.descriptions || {},
      offer: data.offer || '',
      offers: data.offers || {},
      category: data.category || '',
      type: data.type || '',
      image: data.image || '',
      image_metadata: data.image_metadata || null,
      
      // Contact info
      fulladress: data.fulladress || '',
      telephone: data.telephone || '',
      url: data.url || '',
      booking: data.booking || '',
      
      // Location info
      latitude: typeof data.latitude === 'string' ? parseFloat(data.latitude) : data.latitude,
      longitude: typeof data.longitude === 'string' ? parseFloat(data.longitude) : data.longitude,
      distance: typeof data.distance === 'string' ? parseFloat(data.distance) : data.distance,
      
      // Travel times
      timewalk: data.timewalk || '',
      timedrive: data.timedrive || '',
      timebike: data.timebike || '',
      
      // Source info
      source: sourceType === 'event-api' ? 'ticketmaster' : sourceType
    };

    // Add source-specific fields
    switch (sourceType) {
      case 'event':
      case 'event-api':
        return {
          ...standardizedData,
          date: data.date || '',
          hour: (data.hour || '00:00').substring(0, 5),
          place: data.place || '',
          price: data.price || '',
          artiste: data.artiste || '',
          duration: data.duration || null,
          end_time: data.end_time || null,
          idShow: data.idShow || data.idshow || data.show_id || '',
          showId: data.showId || '',
          eventId: data.eventId || ''
        };
      
      case 'standard':
        return {
          ...standardizedData,
          inHotel: data.inHotel || false,
          ouverture_lundi: data.ouverture_lundi || 'non',
          price: data.price || ''
        };
      
      case 'liste':
        return {
          ...standardizedData,
          appstore: data.appstore || '',
          playstore: data.playstore || ''
        };
      
      default:
        return standardizedData;
    }
  }

  public removeFavorite(id: string): void {
    try {
      const index = this.state.items.findIndex(item => item.id === id);
      if (index === -1) return;

      this.state.items.splice(index, 1);
      this.state.lastUpdated = new Date().toISOString();
      this.saveState();
      this.cachedFavorites = null; // Invalidate cache

      logger.debug('Item removed from favorites', {
        category: 'User',
        data: { itemId: id }
      });
    } catch (error) {
      logger.error('Error removing favorite', {
        category: 'Cache',
        data: { error, itemId: id }
      });
    }
  }

  public isFavorite(id: string): boolean {
    return this.state.items.some(item => item.id === id);
  }

  public getFavorites(): FavoriteItem[] {
    // Use cache if it exists
    if (this.cachedFavorites) {
      return this.cachedFavorites;
    }

    // Sort by date added (newest first)
    const items = [...this.state.items].sort((a, b) => 
      new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
    );

    // Cache the result
    this.cachedFavorites = items;

    logger.debug('Getting favorites', {
      category: 'Cache',
      data: {
        itemCount: items.length,
        items: items.map(item => ({
          id: item.id,
          sourceType: item.sourceType,
          metadata: item.metadata,
          data: {
            name: item.data.name,
            category: item.data.category,
            type: item.data.type
          }
        }))
      }
    });

    return items;
  }

  public updateFavoriteTranslation(
    id: string, 
    language: SupportedLanguages, 
    translation: string,
    contentType: 'description' | 'offer' = 'description'
  ): void {
    try {
      const index = this.state.items.findIndex(item => item.id === id);
      if (index === -1) return;

      const item = this.state.items[index];
      
      // Initialize translations object if it doesn't exist
      if (!item.translations) {
        item.translations = { descriptions: {}, offers: {} };
      }
      
      if (contentType === 'description') {
        // Initialize descriptions object if it doesn't exist
        if (!item.translations.descriptions) {
          item.translations.descriptions = {};
        }
        
        // Add the translation
        item.translations.descriptions[language] = translation;
      } else {
        // Initialize offers object if it doesn't exist
        if (!item.translations.offers) {
          item.translations.offers = {};
        }
        
        // Add the translation
        item.translations.offers[language] = translation;
      }
      
      // Update the state
      this.state.lastUpdated = new Date().toISOString();
      this.saveState();
      this.cachedFavorites = null; // Invalidate cache

      logger.debug(`Favorite ${contentType} translation updated`, {
        category: 'Translation',
        data: { 
          itemId: id,
          language,
          contentType,
          translationLength: translation.length
        }
      });
    } catch (error) {
      logger.error(`Error updating favorite ${contentType} translation`, {
        category: 'Cache',
        data: { error, itemId: id, language, contentType }
      });
    }
  }

  public clear(): void {
    this.state = { items: [], lastUpdated: new Date().toISOString() };
    this.saveState();
    this.cachedFavorites = null; // Invalidate cache
    
    logger.debug('Favorites cleared', {
      category: 'Cache'
    });
  }
}

export const favoritesService = FavoritesService.getInstance();